// Shared in-app confirm dialog state + helper. Use confirmAction({...}) as a
// drop-in replacement for window.confirm(). Returns a Promise<boolean>.
//
// Mount <ConfirmDialog /> once near the root (e.g. App.vue or the workspace
// view); state lives in this module so any caller can trigger it.

import { ref } from 'vue'

interface ConfirmRequest {
  title: string
  message?: string
  confirmLabel?: string
  cancelLabel?: string
  // Renders the confirm button in a danger style + shows the warning icon.
  danger?: boolean
}

interface ConfirmState extends ConfirmRequest {
  open: boolean
}

const _initial: ConfirmState = {
  open: false,
  title: '',
}

export const confirmState = ref<ConfirmState>({ ..._initial })

let _resolver: ((v: boolean) => void) | null = null

export function resolveConfirm(value: boolean) {
  const r = _resolver
  _resolver = null
  confirmState.value = { ..._initial }
  if (r) r(value)
}

export function confirmAction(opts: ConfirmRequest): Promise<boolean> {
  // If another confirm is already open, resolve it as cancelled first so we
  // never lose track of a pending promise.
  if (_resolver) resolveConfirm(false)
  return new Promise<boolean>((resolve) => {
    _resolver = resolve
    confirmState.value = {
      open: true,
      title: opts.title,
      message: opts.message ?? '',
      confirmLabel: opts.confirmLabel,
      cancelLabel: opts.cancelLabel,
      danger: !!opts.danger,
    }
  })
}
